import {
  DtoProjectType,
  type DtoProjectType as DtoProjectTypeValue,
} from '@/shared/constants';

import {
  CaptionAsset,
  EditorStarterAsset,
  isCaptionAsset,
} from '../../assets/assets';
import { isCaptionsItem } from '../../items/captions/captions-item-type';
import { EditorStarterItem } from '../../items/item-type';
import { canEditCaptionTiming } from '../../items/captions/caption-timing-edit-policy';
import { clamp } from '../../utils/clamp';
import { generateRandomId } from '../../utils/generate-random-id';
import { EditorState } from '../types';
import { adjustCaptionBoundaryTiming } from './caption-boundary-timing';
import { getMaximumDurationWhenExtendingRightBasedOnNextItem } from './extend-right';
import { updateItemTimings } from './update-item-timings';

interface ApplyCaptionBoundaryTimingParams {
  state: EditorState;
  item: EditorStarterItem;
  asset: EditorStarterAsset | null | undefined;
  edge: 'left' | 'right';
  framePosition: number;
  fps: number;
  projectType?: DtoProjectTypeValue | null;
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const resolveCaptionAssetForItem = (
  state: EditorState,
  item: EditorStarterItem,
  asset: EditorStarterAsset | null | undefined,
): CaptionAsset | undefined => {
  if (!isCaptionsItem(item)) {
    return undefined;
  }

  const stateAsset = state.undoableState.assets[item.assetId];
  if (isCaptionAsset(stateAsset)) {
    return stateAsset;
  }

  const fallbackAsset = asset ?? undefined;
  return isCaptionAsset(fallbackAsset) ? fallbackAsset : undefined;
};

const createCaptionClipId = () => Date.now() + Math.floor(Math.random() * 1000);

const createCaptionAssetId = (
  assets: Record<string, EditorStarterAsset>,
): string => {
  let assetId = '';

  do {
    assetId = `caption-c${generateRandomId()}`;
  } while (assets[assetId]);

  return assetId;
};

const getActiveCaptionAssetReferenceCount = (
  state: EditorState,
  assetId: string,
): number => {
  const activeItemIds = new Set<string>();
  for (const track of state.undoableState.tracks) {
    for (const itemId of track.items) {
      activeItemIds.add(itemId);
    }
  }

  let count = 0;
  for (const itemId of activeItemIds) {
    const item = state.undoableState.items[itemId];
    if (isCaptionsItem(item) && item.assetId === assetId) {
      count++;
    }
  }

  return count;
};

const isolateAdjustedCaptionAsset = ({
  state,
  item,
  asset,
}: {
  state: EditorState;
  item: EditorStarterItem;
  asset: CaptionAsset;
}): { item: EditorStarterItem; asset: CaptionAsset } => {
  if (
    !isCaptionsItem(item) ||
    getActiveCaptionAssetReferenceCount(state, asset.id) <= 1
  ) {
    return { item, asset };
  }

  const assetId = createCaptionAssetId(state.undoableState.assets);
  const nextAsset: CaptionAsset = {
    ...asset,
    id: assetId,
    clipId: createCaptionClipId(),
    filename: `${assetId}.json`,
    remoteUrl: null,
    remoteFileKey: null,
  };

  return {
    item: {
      ...item,
      assetId,
    },
    asset: nextAsset,
  };
};

const withAdjustedCaptionRenderMeta = (
  item: EditorStarterItem,
  asset: EditorStarterAsset,
): EditorStarterItem => {
  if (!isCaptionsItem(item) || !isCaptionAsset(asset) || !item.renderMeta) {
    return item;
  }

  return {
    ...item,
    renderMeta: {
      ...item.renderMeta,
      ...(isFiniteNumber(asset.videoStart)
        ? { videoStart: asset.videoStart }
        : {}),
      ...(isFiniteNumber(asset.videoEnd) ? { videoEnd: asset.videoEnd } : {}),
    },
  };
};

export const canApplyCaptionBoundaryTiming = ({
  item,
  asset,
  projectType,
}: Pick<
  ApplyCaptionBoundaryTimingParams,
  'item' | 'asset' | 'projectType'
>): boolean => {
  const captionAsset = asset ?? undefined;
  if (!isCaptionsItem(item) || !isCaptionAsset(captionAsset)) {
    return false;
  }

  return canEditCaptionTiming({
    projectType,
    itemType: item.type,
    assetType: captionAsset.type,
  });
};

export const applyCaptionBoundaryTiming = ({
  state,
  item,
  asset,
  edge,
  framePosition,
  fps,
  projectType,
}: ApplyCaptionBoundaryTimingParams): EditorState | null => {
  const captionAsset = resolveCaptionAssetForItem(state, item, asset);
  if (!isCaptionsItem(item) || !captionAsset) {
    return null;
  }

  if (
    !canEditCaptionTiming({
      projectType,
      itemType: item.type,
      assetType: captionAsset.type,
    })
  ) {
    return null;
  }

  const adjusted = adjustCaptionBoundaryTiming({
    item,
    asset: captionAsset,
    edge,
    framePosition,
    fps,
  });
  const isolated = isolateAdjustedCaptionAsset({
    state,
    item: adjusted.item,
    asset: adjusted.asset,
  });
  const adjustedItemWithRenderMeta = withAdjustedCaptionRenderMeta(
    isolated.item,
    isolated.asset,
  );
  const adjustedItem =
    projectType === DtoProjectType.agentVideoCaption &&
    isCaptionsItem(adjustedItemWithRenderMeta)
      ? {
          ...adjustedItemWithRenderMeta,
          hasUserDefinedPresentation: true,
        }
      : adjustedItemWithRenderMeta;

  return {
    ...state,
    undoableState: {
      ...state.undoableState,
      items: {
        ...state.undoableState.items,
        [adjustedItem.id]: adjustedItem,
      },
      assets: {
        ...state.undoableState.assets,
        [isolated.asset.id]: isolated.asset,
      },
    },
  };
};

interface CalculateCaptionRightBoundaryItemParams {
  item: EditorStarterItem;
  initialFrom: number;
  initialDurationInFrames: number;
  offsetInFrames: number;
  visibleFrames: number;
  trackItemsSorted: string[];
  itemIndex: number;
  items: Record<string, EditorStarterItem>;
  isMagneticExtendAllowed?: boolean;
}

export const calculateCaptionRightBoundaryItem = ({
  item,
  initialFrom,
  initialDurationInFrames,
  offsetInFrames,
  visibleFrames,
  trackItemsSorted,
  itemIndex,
  items,
  isMagneticExtendAllowed = false,
}: CalculateCaptionRightBoundaryItemParams): EditorStarterItem => {
  const maxDurationBeforeNextItem = isMagneticExtendAllowed
    ? Infinity
    : getMaximumDurationWhenExtendingRightBasedOnNextItem({
        trackItemsSorted,
        items,
        itemIndex,
        initialFrom,
      });
  const maxDuration = Math.min(
    maxDurationBeforeNextItem,
    visibleFrames - initialFrom,
  );
  const newDurationInFrames = clamp({
    value: initialDurationInFrames + offsetInFrames,
    min: 1,
    max: maxDuration,
  });

  return updateItemTimings({
    item,
    newDurationInFrames,
    newFrom: item.from,
  });
};
